import { action } from '@ember/object';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { or } from 'ember-truth-helpers';
import BvSelect from 'volta/components/bv-select';
import BvSelectMulti from 'volta/components/bv-select-multi';
import FiltersStatusOption from 'volta/components/filters/status-option';
import orElse from 'volta/helpers/or-else';

import type { SafeString } from '@ember/template';

export interface IStatusFilterComponentArgs {
  possibleValues?: IStatusOption[];
  renderInPlace?: boolean;
  horizontalPosition?: 'right' | 'left' | 'auto';
  matchTriggerWidth?: boolean;
  hideCount?: boolean;
  allowClear?: boolean;
  label?: string | SafeString | undefined;
}

export interface FiltersStatusFilterSignature {
  Args: IFilterArgs<IStatusFilterComponentArgs> &
    Pick<
      IStatusFilterComponentArgs,
      'label' | 'matchTriggerWidth' | 'renderInPlace' | 'allowClear' | 'horizontalPosition'
    >;
}

export default class FiltersStatusFilterComponent extends Component<FiltersStatusFilterSignature> {
  get selected() {
    const { values = [], multiple, componentArgs } = this.args;

    if (multiple) {
      return componentArgs.possibleValues?.filter((o) => o && values.includes(o.key));
    } else {
      return values.length > 0
        ? componentArgs.possibleValues?.find((o) => o.key === values[0])
        : undefined;
    }
  }

  private getValues(options: (IStatusOption | undefined)[]) {
    return options.reduce((acc: string[], o) => {
      const v = o?.['key'];
      if (isPresent(v)) {
        acc.push(v as string);
      }
      return acc;
    }, []);
  }

  @action
  valuesDidChange(values: IStatusOption[]) {
    this.args.onChange?.(this.getValues(values));
  }

  @action
  singleValueDidChange(value?: IStatusOption) {
    this.args.onChange?.(value ? [value.key] : []);
  }

  <template>
    {{#if @multiple}}
      <BvSelectMulti
        @label={{@label}}
        @fill={{true}}
        @options={{@componentArgs.possibleValues}}
        @selected={{this.selected}}
        @optionKey='value'
        @searchField='label'
        @searchEnabled={{true}}
        @renderInPlace={{orElse @componentArgs.renderInPlace @renderInPlace}}
        @horizontalPosition={{or @componentArgs.horizontalPosition @horizontalPosition}}
        @matchTriggerWidth={{orElse
          (orElse @componentArgs.matchTriggerWidth @matchTriggerWidth)
          true
        }}
        @allowClear={{orElse @componentArgs.allowClear @allowClear}}
        @onChange={{this.valuesDidChange}}
        @dataName={{@name}}
        as |status|
      >

        <FiltersStatusOption @status={{status}} @hideCount={{@componentArgs.hideCount}} />
      </BvSelectMulti>
    {{else}}
      <BvSelect
        @label={{@label}}
        @fill={{true}}
        @options={{@componentArgs.possibleValues}}
        @selected={{this.selected}}
        @optionKey='value'
        @searchField='label'
        @searchEnabled={{true}}
        @renderInPlace={{orElse @componentArgs.renderInPlace @renderInPlace}}
        @horizontalPosition={{or @componentArgs.horizontalPosition @horizontalPosition}}
        @matchTriggerWidth={{orElse
          (orElse @componentArgs.matchTriggerWidth @matchTriggerWidth)
          true
        }}
        @allowClear={{@allowClear}}
        @onChange={{this.singleValueDidChange}}
        @dataName={{@name}}
        as |status|
      >
        <FiltersStatusOption @status={{status}} @hideCount={{@componentArgs.hideCount}} />
      </BvSelect>
    {{/if}}
  </template>
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Filters::StatusFilter': typeof FiltersStatusFilterComponent;
    'filters/status-filter': typeof FiltersStatusFilterComponent;
  }
}
